/**
 * AppEnvironment — web mirror of `Sources/App/AppEnvironment.swift`.
 *
 * Wires the two seams together (auth + store), owns the loaded snapshot, and is
 * the single place the backend is swapped (Mock ↔ Supabase). It exposes a tiny
 * observable surface (subscribe/getState) so React can bind via
 * `useSyncExternalStore` — the analogue of Swift's `@Observable`.
 */

import type { AuthBoundary, AuthSession } from '../auth/AuthBoundary';
import { BrainAuth, BrainAuthError, StubAuth } from '../auth/AuthBoundary';
import {
  brainConfigFromEnvironment,
  brainCredentialsFromEnvironment,
  selectedBackend,
  type Backend,
} from '../config';
import {
  emptySnapshot,
  pollSnapshots,
  type FactorySnapshot,
  type FactoryStore,
} from '../store/FactoryStore';
import { MockFactoryStore } from '../store/MockFactoryStore';
import { SupabaseFactoryStore } from '../store/SupabaseFactoryStore';
import type { MemoriesStore } from '../store/MemoriesStore';
import { MockMemoriesStore } from '../store/MockMemoriesStore';
import { BrainMemoriesStore } from '../store/BrainMemoriesStore';
import type { BridgeTokensStore } from '../store/BridgeTokensStore';
import { MockBridgeTokensStore } from '../store/MockBridgeTokensStore';
import { BrainBridgeTokensStore } from '../store/BrainBridgeTokensStore';
import type { FilesStore } from '../store/FilesStore';
import { MockFilesStore } from '../store/MockFilesStore';
import { BrainFilesStore } from '../store/BrainFilesStore';
import type { SessionsStore } from '../store/SessionsStore';
import { MockSessionsStore } from '../store/MockSessionsStore';
import { BrainSessionsStore } from '../store/BrainSessionsStore';
import type { FactoryCommandInsert } from '../models/factory';
import { taskStatus } from '../models/factory';

/** Gates the top-level UI: `booting` while we try to resume a stored session,
 * `signedOut` → SignIn, `signedIn` → RootView. Mock is always `signedIn`. */
export type AuthPhase = 'booting' | 'signedOut' | 'signedIn';

export interface AppState {
  backend: Backend;
  authPhase: AuthPhase;
  session: AuthSession | null;
  snapshot: FactorySnapshot;
  isLoading: boolean;
  lastError: string | null;
}

export class AppEnvironment {
  readonly auth: AuthBoundary;
  readonly store: FactoryStore;
  /** Seam 3: Brain memories (read-only). MockMemoriesStore in mock mode;
   * BrainMemoriesStore (Worker + brain_ token) in the live path. The Brain tab
   * reads through this; it is NOT part of the polled factory snapshot. */
  readonly memories: MemoriesStore;
  /** Seam 4: bridge tokens (engine pairing). MockBridgeTokensStore in mock mode;
   * BrainBridgeTokensStore (Worker + brain_ token) in the live path. The Setup
   * tab reads/writes through this; also NOT part of the polled snapshot. */
  readonly bridgeTokens: BridgeTokensStore;
  /** Seam 5: Brain files (owner-only Mac control). MockFilesStore in mock mode;
   * BrainFilesStore (Worker + brain_ token → Mac bridge) in the live path. The
   * Brain ▸ Files view reads through this; NOT part of the polled snapshot. */
  readonly files: FilesStore;
  /** Seam 6: Brain tmux sessions (owner-only Mac control). MockSessionsStore in
   * mock mode; BrainSessionsStore (Worker + brain_ token → Mac bridge) in the
   * live path. The Brain ▸ Sessions view reads through this; NOT polled. */
  readonly sessions: SessionsStore;
  readonly backend: Backend;

  private authPhase: AuthPhase;
  private session: AuthSession | null = null;
  private snapshot: FactorySnapshot = emptySnapshot();
  private isLoading = false;
  private lastError: string | null = null;

  private listeners = new Set<() => void>();
  private cachedState: AppState;
  private stopPolling: (() => void) | null = null;

  constructor(
    auth: AuthBoundary,
    store: FactoryStore,
    memories: MemoriesStore,
    bridgeTokens: BridgeTokensStore,
    files: FilesStore,
    sessions: SessionsStore,
    backend: Backend,
  ) {
    this.auth = auth;
    this.store = store;
    this.memories = memories;
    this.bridgeTokens = bridgeTokens;
    this.files = files;
    this.sessions = sessions;
    this.backend = backend;
    // Mock is always "signed in"; the real path boots then resolves.
    this.authPhase = backend === 'mock' ? 'signedIn' : 'booting';
    this.cachedState = this.buildState();
  }

  // MARK: - Backend selection (the ONE go-live switch)
  //
  // DEFAULT is Mock so the app builds/runs fully offline. Set
  // VITE_FACTORY_BACKEND=supabase to select the real Supabase + BrainAuth path.
  static resolved(): AppEnvironment {
    if (selectedBackend === 'supabase') {
      const config = brainConfigFromEnvironment();
      const auth = new BrainAuth(config);
      const store = new SupabaseFactoryStore(config, () => auth.currentSupabaseAccessToken());
      // Memories come straight from the Worker with the brain_ token — NOT the
      // Supabase JWT (no RLS on that surface).
      const memories = new BrainMemoriesStore(config, () => auth.currentBrainToken());
      // Bridge tokens are minted/listed/revoked on the Worker with the same
      // brain_ token (the bridge-token API is a Worker surface, not Supabase).
      const bridgeTokens = new BrainBridgeTokensStore(config, () => auth.currentBrainToken());
      // Files + tmux are owner-only Mac control on the Worker with the same
      // brain_ token (dispatched to the operator's bridge, engine required).
      const files = new BrainFilesStore(config, () => auth.currentBrainToken());
      const sessions = new BrainSessionsStore(config, () => auth.currentBrainToken());
      return new AppEnvironment(auth, store, memories, bridgeTokens, files, sessions, 'supabase');
    }
    return new AppEnvironment(
      new StubAuth(),
      new MockFactoryStore(),
      new MockMemoriesStore(),
      new MockBridgeTokensStore(),
      new MockFilesStore(),
      new MockSessionsStore(),
      'mock',
    );
  }

  // MARK: - Observable surface (for useSyncExternalStore)

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (): AppState => this.cachedState;

  private buildState(): AppState {
    return {
      backend: this.backend,
      authPhase: this.authPhase,
      session: this.session,
      snapshot: this.snapshot,
      isLoading: this.isLoading,
      lastError: this.lastError,
    };
  }

  private emit(): void {
    this.cachedState = this.buildState();
    for (const l of this.listeners) l();
  }

  // MARK: - Lifecycle

  async bootstrap(): Promise<void> {
    // Mock: launch straight into the app, no login.
    if (this.backend === 'mock') {
      this.session = await this.auth.signIn();
      this.authPhase = 'signedIn';
      this.emit();
      await this.refresh();
      this.startLiveUpdates();
      return;
    }

    // Supabase: try to resume from the persisted `brain_` token in localStorage.
    try {
      this.session = await this.auth.signIn();
    } catch {
      // No stored token → try a best-effort headless sign-in from env creds
      // (never hardcoded), else drop to the SignIn screen.
      this.session = this.auth.currentSession();
      if (!this.session && this.auth instanceof BrainAuth) {
        const { email, password } = brainCredentialsFromEnvironment();
        if (email && password) {
          try {
            this.session = await this.auth.login(email, password);
          } catch (e) {
            this.lastError = e instanceof Error ? e.message : String(e);
          }
        }
      }
    }
    this.authPhase = this.session ? 'signedIn' : 'signedOut';
    this.emit();
    if (this.authPhase === 'signedIn') {
      await this.refresh();
      this.startLiveUpdates();
    }
  }

  /** Drive the real Brain login from the SignIn screen. On success, transitions
   * into the app (refresh + live updates). Throws on failure so SignIn can show
   * an inline error. No credential is ever stored in source — the user types
   * their own. */
  async signInWithCredentials(email: string, password: string): Promise<void> {
    if (!(this.auth instanceof BrainAuth)) {
      throw new BrainAuthError('needsLogin', 'Sign-in is only available on the Supabase backend.');
    }
    this.session = await this.auth.login(email, password);
    this.authPhase = 'signedIn';
    this.lastError = null;
    this.emit();
    await this.refresh();
    this.startLiveUpdates();
  }

  /** Sign out: stop live updates, clear the localStorage `brain_` token via the
   * auth boundary, reset state, and return to the SignIn screen. */
  async signOut(): Promise<void> {
    this.stopLiveUpdates();
    await this.auth.signOut();
    this.session = null;
    this.snapshot = emptySnapshot();
    this.lastError = null;
    this.authPhase = 'signedOut';
    this.emit();
  }

  /** Subscribe to the store's live-update stream. When the store provides
   * `liveUpdates` (SupabaseFactoryStore → Supabase Realtime primary + polling
   * fallback) that owns the loop; otherwise (MockFactoryStore) we poll. Either
   * way the callback/observer plumbing is identical. Idempotent. */
  startLiveUpdates(intervalMs = 5000): void {
    if (this.stopPolling) return;
    const onSnapshot = (snap: FactorySnapshot) => {
      this.snapshot = snap;
      this.emit();
    };
    this.stopPolling = this.store.liveUpdates
      ? this.store.liveUpdates(onSnapshot, { intervalMs })
      : pollSnapshots(this.store, onSnapshot, intervalMs);
  }

  stopLiveUpdates(): void {
    this.stopPolling?.();
    this.stopPolling = null;
  }

  async refresh(): Promise<void> {
    this.isLoading = true;
    this.lastError = null;
    this.emit();
    try {
      this.snapshot = await this.store.fetchSnapshot();
    } catch (e) {
      this.lastError = e instanceof Error ? e.message : String(e);
    }
    this.isLoading = false;
    this.emit();
  }

  /** Enqueue a command, then refresh so the mock read-model reflects it. */
  async submit(command: FactoryCommandInsert): Promise<void> {
    try {
      await this.store.submit(command);
      await this.refresh();
    } catch (e) {
      this.lastError = e instanceof Error ? e.message : String(e);
      this.emit();
    }
  }

  // MARK: - Derived views over the snapshot

  get tenantId(): string {
    return this.session?.tenantId ?? StubAuth.fakeTenantId;
  }

  pendingApprovals(state: AppState = this.cachedState) {
    return state.snapshot.approvals;
  }

  activeTasks(state: AppState = this.cachedState) {
    return state.snapshot.tasks.filter((t) =>
      ['running', 'queued', 'blocked'].includes(taskStatus(t.status)),
    );
  }
}
